import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Users, ChefHat, Wine, Star, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth, API } from '@/App';
import { toast } from 'sonner';

// Experience data with images from Shopify
const EXPERIENCES = [
  {
    id: 'cream-tea-masterclass',
    name: 'Cream Tea Masterclass',
    shortName: 'Cream Tea',
    price: 45,
    duration: '3 hours',
    description: 'Learn to make your own afternoon tea, including lemonade scones, shortbread, and mini Victoria sponges. Enjoy what you create with tea and conversation with award-winning chef Greg Wixted.',
    includes: [
      'Framed signed certificate',
      'Printed recipes',
      'Cream tea baking kit',
      'Leftover goodies to take home'
    ],
    image: 'https://www.cakerywonderland.com/cdn/shop/files/britain-loves-baking-academy-3-hour-cream-tea-masterclassbaking-masterclasswonderland-experiences-1726264.png?v=1754436424&width=800',
    capacity: '12 guests max'
  },
  {
    id: 'bridgerton-masterclass',
    name: 'Bridgerton Cake Decorating & Cocktails',
    shortName: 'Bridgerton',
    price: 65,
    duration: '2.5 hours',
    description: 'Step into Regency elegance with our Bridgerton-inspired cake decorating masterclass. Create period-inspired designs while sipping themed cocktails. Perfect for groups and special occasions.',
    includes: [
      'Signature Bridgerton cocktails',
      'Your decorated cake to take home',
      'Cakery apron (yours to keep)',
      'Framed certificate signed by Lady Whistledown'
    ],
    image: 'https://www.cakerywonderland.com/cdn/shop/files/regency-elegance-bridgerton-inspired-cake-decorating-masterclass-and-cocktailswonderland-experienceswonderland-experiences-294578.png?v=1744126383&width=800',
    capacity: '9 guests max'
  },
  {
    id: 'dessert-tower-experience',
    name: 'Dessert Tower Making & Cocktails',
    shortName: 'Dessert Tower',
    price: 90,
    duration: '3 hours',
    description: 'Build your own sky-high dessert tower featuring macarons, choux buns, cronuts, brownies, and doughnut balls. Sip Tweeny Martinis while our pastry chefs guide you through this decadent experience.',
    includes: [
      'Mini martinis throughout',
      'Dessert Tower Architect certificate',
      'Exclusive recipe booklet',
      'Your magnificent creation to take home'
    ],
    image: 'https://www.cakerywonderland.com/cdn/shop/files/unleash-your-inner-pastry-chef-dessert-tower-making-cocktail-experiencedessert-experiencecakery-wonderland-events-991485.jpg?v=1739058779&width=800',
    capacity: '10 guests max'
  }
];

const ExperiencesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [bookingDate, setBookingDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [loading, setLoading] = useState(false);

  const timeSlots = ['10:00 AM', '2:00 PM', '6:00 PM'];

  const handleBookExperience = async () => {
    if (!user) {
      toast.error('Please login to book an experience');
      navigate('/login');
      return;
    }

    if (!bookingDate || !timeSlot) {
      toast.error('Please select a date and time');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/experiences/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          experience_id: selectedExperience.id,
          experience_name: selectedExperience.name,
          booking_date: bookingDate,
          time_slot: timeSlot,
          price: selectedExperience.price,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create booking');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <div className="min-h-screen bg-background pt-16" data-testid="experiences-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-gradient py-20">
        <div className="absolute top-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Calendar className="w-12 h-12 text-primary mx-auto mb-6 animate-float" />
            <h1 className="text-5xl font-heading font-bold text-white mb-4" data-testid="experiences-title">
              Book Your Experience
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto font-body">
              Immersive baking masterclasses, cocktails, and unforgettable memories. Perfect for groups, couples, or solo adventurers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Experiences Grid */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {EXPERIENCES.map((experience, index) => (
              <motion.div
                key={experience.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-background-paper rounded-2xl overflow-hidden border border-primary/20 hover:border-primary/50 transition-all group"
                data-testid={`experience-card-${experience.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={experience.image}
                    alt={experience.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-primary text-white px-4 py-2 rounded-full font-bold text-lg">
                    £{experience.price}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-heading font-bold text-white mb-2">{experience.name}</h3>
                  
                  <div className="flex items-center gap-4 text-text-secondary text-sm mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-primary" />
                      {experience.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-primary" />
                      {experience.capacity}
                    </span>
                  </div>

                  <p className="text-text-secondary font-body text-sm mb-4 line-clamp-3">
                    {experience.description}
                  </p>

                  <div className="mb-6">
                    <p className="text-white font-semibold text-sm mb-2">What's Included:</p>
                    <ul className="space-y-1">
                      {experience.includes.slice(0, 3).map((item, i) => (
                        <li key={i} className="text-text-secondary text-sm flex items-start gap-2">
                          <Star className="w-3 h-3 text-primary mt-1 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button
                    onClick={() => setSelectedExperience(experience)}
                    className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-3"
                    data-testid={`book-${experience.id}-btn`}
                  >
                    Book Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {selectedExperience && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="booking-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-background-paper rounded-2xl max-w-lg w-full p-8 border border-primary/20"
          >
            <h3 className="text-2xl font-heading font-bold text-white mb-2">
              Book {selectedExperience.name}
            </h3>
            <p className="text-primary text-xl font-bold mb-6">£{selectedExperience.price} per person</p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-white text-sm font-medium mb-2">Select Date</label>
                <Input
                  type="date"
                  value={bookingDate}
                  onChange={(e) => setBookingDate(e.target.value)}
                  min={getMinDate()}
                  className="w-full bg-background-elevated border-primary/30 text-white rounded-lg"
                  data-testid="booking-date-input"
                />
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">Select Time</label>
                <div className="grid grid-cols-3 gap-2">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => setTimeSlot(slot)}
                      className={`py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                        timeSlot === slot
                          ? 'bg-primary text-white'
                          : 'bg-background-elevated text-white hover:bg-primary/20 border border-primary/30'
                      }`}
                      data-testid={`time-slot-${slot.replace(/\s/g, '-')}`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setSelectedExperience(null);
                  setBookingDate('');
                  setTimeSlot('');
                }}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10 rounded-full"
                data-testid="cancel-booking-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBookExperience}
                disabled={loading || !bookingDate || !timeSlot}
                className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-full"
                data-testid="confirm-booking-btn"
              >
                {loading ? 'Processing...' : `Pay £${selectedExperience.price}`}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ExperiencesPage;
